import dynamic from "next/dynamic";
import Theme from "../../components/Theme";
import Hero from "../../components/organisms/features/Hero";
import HighlightFeaturesCards from "../../components/organisms/common/HighlightFeaturesCards";
import FeaturedImage from "../../components/organisms/features/FeaturedImage";
import CutoutFeaturedImage from "../../components/organisms/common/CutoutFeaturedImage";
import BoxShadowCard from "../../components/organisms/common/BoxShadowCard";
import {
  font,
  lineHeight,
  viewport,
} from "../../styles/cssinjs/helpers/variables";
import Video from "../../components/atoms/common/Video";
const AppStorage = dynamic(() => import("../../components/AppStorage"));
const FAQ = dynamic(() => import("../../components/organisms/common/FAQ"));
const WideCTA = dynamic(() =>
  import("../../components/organisms/common/WideCTA")
);
const People = dynamic(() =>
  import("../../components/organisms/common/People")
);
const CTA = dynamic(() => import("../../components/organisms/common/CTA"));
const FeaturesShowCaseCards = dynamic(() =>
  import("../../components/organisms/features/FeaturesShowCaseCards")
);
const Steps = dynamic(() =>
  import("../../components/organisms/features/Steps")
);
const SecondFold = dynamic(() =>
  import("../../components/organisms/features/SecondFold")
);
const Schema = dynamic(() =>
  import("../../components/organisms/common/Schema")
);
const FeaturesGrid = dynamic(() =>
  import("../../components/organisms/common/FeaturesGrid")
);

export async function getStaticPaths({ locales }) {
  // Fetch All Path
  const res = await fetch(`${process.env.JSON_PATH}/routes.json`);
  const data = await res.json();

  // Specific Paths
  let routes = data.features.all;

  // Variable for returning path
  let paths = [];

  // Create Paths with locales
  routes.forEach((path) => {
    for (const locale of locales) {
      paths.push({
        params: {
          page: path,
        },
        locale,
      });
    }
  });
  // return to getStaticProps()
  return {
    paths,
    fallback: false,
  };
}
export async function getStaticProps({ params, locale }) {
  let allData = {};

  // Fetch
  const headerResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/common/header.json`
  );
  const footerResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/common/footer.json`
  );
  const contentResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/features/${params.page}.json`
  );

  // Catch Error
  if (contentResponse.status === 404) {
    return {
      notFound: true,
    };
  }

  // Convert
  allData.header = await headerResponse.json();
  allData.content = await contentResponse.json();
  allData.footer = await footerResponse.json();

  // Wrap
  const header = {
    header: allData.header,
  };
  const footer = {
    footer: allData.footer,
  };

  // Combine all data
  const data = await {
    ...header,
    ...footer,
    ...allData.content,
  };

  return {
    props: {
      data,
      pageName: params.page,
      lang: locale,
    },
  };
}
const FeaturesGridSection = ({ data, isIconInline, helper }) => {
  return (
    <section
      className={`${
        helper.pageId === "features-virtual-meetings"
          ? "bg-light-blue-grey"
          : "bg-light"
      }`}
    >
      <FeaturesGrid
        data={data.list}
        colCount={data.colCount}
        isIconsInline={isIconInline}
      />
      <style>{`
    .card--default .icon-title-wrap figure {
      width: 24px;
    }
  `}</style>
    </section>
  );
};
// Local Components
const CutoutFeaturedImageComp = ({ data, isNewDesign }) => {
  return (
    <CutoutFeaturedImage
      data={data}
      className={`md:pb-10 md:pt-5 ${isNewDesign ? "lg:pb-15" : "lg:pb-20"} `}
    />
  );
};

const VideoSection = ({ data, toggleVideo, isVideoModalVisible, videoId }) => {
  return (
    <section className="container video-section ">
      <div className="grid--default has-align-middle md:fx md:fx--w">
        <Video
          data={data.video}
          classNames="featured-video md:w-100"
          toggleVideo={(id) => {
            toggleVideo(id);
          }}
          isVideoModalVisible={isVideoModalVisible}
          videoId={videoId}
        />
        <article className="text-left lg:maxw-md fx--o-2">
          <h2
            className="text-lg lg:mb-3 md:mb-2 md:mt-5"
            dangerouslySetInnerHTML={{ __html: data.title }}
          />
          {data.hasOwnProperty("desc") &&
            data.desc.map((descItem, index) => {
              return (
                <p
                  className="lg:maxw-md lg:mt-2 lg:mb-2 md:mt-2"
                  key={index}
                  dangerouslySetInnerHTML={{ __html: descItem }}
                />
              );
            })}
        </article>{" "}
      </div>
    </section>
  );
};

const BoxShadowCardSmallIconSection = ({ data, className, pageName }) => {
  return (
    <section className={className}>
      {data.title && (
        <h2
          className={`container lg:text-center md:text-center lg:mb-10 md:mb-5 lg:maxw-xl `}
          dangerouslySetInnerHTML={{ __html: data.title }}
        />
      )}
      <BoxShadowCard
        data={data.list}
        hasLink={data.hasLink}
        isIconsInline={!(pageName === "industries--salon")}
      />
      <style jsx>{`
        h2 {
          font-size: ${font.size.lg[4]} !important;
          line-height: ${lineHeight[16]} !important;
        }
        @media (max-width: ${viewport.mobile.md}) {
          h2 {
            font-size: ${font.size.lg[1]} !important;
            line-height: ${lineHeight[10]} !important;
            text-align: center !important;
          }
        }
      `}</style>
    </section>
  );
};

export default function Appointments({ data, pageName, lang, nonce }) {
  const seo = data.page_meta;
  const content = data.page_content;
  const helper = data.page_helper;
  const header = data.header;
  const footer = data.footer;

  return (
    <>
      <Theme
        header={header}
        footer={footer}
        className={`features-faq ${helper.pageName}`}
        seo={seo}
        nonce={nonce}
      >
        <AppStorage pageName={`Features Sub Page - ${pageName}`} />
        <Hero data={content.hero} id={helper.pageId} />
        {content.hasOwnProperty("secondFold") ? (
          <SecondFold data={content.secondFold} />
        ) : (
          ""
        )}

        {content.hasOwnProperty("cutoutFeaturedImage") ? (
          <CutoutFeaturedImageComp
            data={content.cutoutFeaturedImage}
            isNewDesign={true}
          />
        ) : (
          ""
        )}

        {content.hasOwnProperty("videoSection") ? (
          <section className="bg-light-blue-grey lg:pb-20  md:pb-10">
            <VideoSection data={content.videoSection} />
          </section>
        ) : (
          <></>
        )}

        {content.hasOwnProperty("industrySpecificFeatures") && (
          <BoxShadowCardSmallIconSection
            data={content.industrySpecificFeatures}
            pageName={helper.pageId}
            className=" bg-offset bg-offset--adapt lg:pb-10 md:pb-1"
          />
        )}

        {helper.pageId === "features-virtual-meetings" ||
          (helper.pageId === "features-live-receptionist" && (
            <FeaturesGridSection
              data={content.featuresGrid}
              isIconInline={true}
              helper={helper}
            />
          ))}

        {content.hasOwnProperty("featuresShowcaseCards") && (
          <section className={`bg-offset-top pb-10`}>
            <FeaturesShowCaseCards data={content.featuresShowcaseCards} />
          </section>
        )}

        <section
          className={`md:mb-10 ${
            helper.pageId === "features-virtual-meetings" ||
            helper.pageId === "features-booking-page"
              ? "lg:pt-20 "
              : "lg:pt-10 "
          }`}
        >
          <div className="container">
            {content.hasOwnProperty("featuredGifSections") ? (
              <FeaturedImage data={content.featuredGifSections} />
            ) : (
              <FeaturedImage data={content.featuredImageSections} />
            )}
          </div>
        </section>

        {content.hasOwnProperty("stepsSection") ? (
          <Steps data={content.stepsSection} />
        ) : (
          ""
        )}

        {content.hasOwnProperty("cta") ? (
          <CTA
            data={content.cta}
            className={`${
              helper.pageId === "features-virtual-meetings"
                ? ""
                : "bg-offset-bottom"
            }`}
            colorScheme={
              helper.pageId === "features-virtual-meetings"
                ? "grey-white-blue"
                : ""
            }
          />
        ) : (
          ""
        )}

        {content.hasOwnProperty("ctaTwo") ? (
          <CTA
            data={content.ctaTwo}
            className={`${
              helper.pageId === "features-booking-page"
                ? ""
                : "bg-offset-bottom"
            }`}
            colorScheme={
              helper.pageId === "features-booking-page" ? "grey-white-blue" : ""
            }
          />
        ) : (
          ""
        )}

        {content.highlightFeaturesCards && (
          <HighlightFeaturesCards
            data={content.highlightFeaturesCards}
            classes={`${
              helper.pageId === "features-virtual-meetings" ||
              helper.pageId === "features-booking-page"
                ? "bg-offset-bottom"
                : ""
            }`}
          />
        )}
        {content.hasOwnProperty("ctaWithAudio") ? (
          <CTA
            data={content.ctaWithAudio}
            className="pos-rel bg-light-blue-grey md:pb-10 lg:pb-20 cta"
            ctaBgLightGreenColor={true}
          />
        ) : (
          ""
        )}

        {content.hasOwnProperty("faqs") ? (
          <FAQ
            className={`bg-offset-top md:pb-10 lg:pb-15 ${
              helper.pageId === "features-virtual-meetings" ||
              helper.pageId === "features-booking-page"
                ? "bg-light-blue-grey"
                : ""
            }`}
            data={content.faqs}
          />
        ) : (
          ""
        )}

        <People
          className={`${
            helper.pageId === "features-desktop-app"
              ? "bg-light"
              : helper.pageId === "features-virtual-meetings" ||
                helper.pageId === "features-booking-page" ||
                helper.pageId === "features-your-branded-app" ||
                helper.pageId === "features-live-receptionist"
              ? "d-none"
              : ""
          }`}
        />

        {!content.hasOwnProperty("wideCTA") ? (
          ""
        ) : (
          <WideCTA data={content.wideCTA} />
        )}
      </Theme>
      {content.hasOwnProperty("faqs") ? (
        <Schema data={content.faqs.list} />
      ) : (
        ""
      )}
    </>
  );
}
